import { useMemo } from "react";
import { formatEuroCompact } from "@/lib/heroDocumentReports";
import "./auswertungen.css";

export interface CustomerRevenuePieItem {
  key: string;
  label: string;
  value: number;
}

interface Slice {
  key: string;
  label: string;
  value: number;
  percent: number;
  color: string;
  path: string;
}

const TOP_SLICE_COUNT = 10;

const SLICE_COLORS = [
  "var(--eh-accent)",
  "var(--eh-green)",
  "var(--eh-orange)",
  "var(--eh-purple)",
  "var(--eh-yellow)",
  "var(--eh-red)",
  "color-mix(in srgb, var(--eh-accent) 55%, var(--eh-green))",
  "color-mix(in srgb, var(--eh-orange) 55%, var(--eh-red))",
  "color-mix(in srgb, var(--eh-purple) 55%, var(--eh-accent))",
  "color-mix(in srgb, var(--eh-green) 55%, var(--eh-yellow))",
  "var(--eh-label-tertiary)",
];

function formatPercentDe(value: number): string {
  return `${value.toFixed(1).replace(".", ",")}\u00a0%`;
}

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** Donut-Segment von startAngle bis endAngle (Grad, 0 = oben, Uhrzeigersinn). */
function donutSegmentPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const sweep = endAngle - startAngle;
  if (sweep <= 0) return "";
  if (sweep >= 359.999) {
    // Vollkreis: zwei Halbkreis-Bögen (SVG-Arc braucht large-arc bei 360° nicht zuverlässig)
    const o1 = polar(cx, cy, outerR, 0);
    const o2 = polar(cx, cy, outerR, 180);
    const i1 = polar(cx, cy, innerR, 180);
    const i2 = polar(cx, cy, innerR, 0);
    return [
      `M ${o1.x} ${o1.y}`,
      `A ${outerR} ${outerR} 0 1 1 ${o2.x} ${o2.y}`,
      `A ${outerR} ${outerR} 0 1 1 ${o1.x} ${o1.y}`,
      `L ${i2.x} ${i2.y}`,
      `A ${innerR} ${innerR} 0 1 0 ${i1.x} ${i1.y}`,
      `A ${innerR} ${innerR} 0 1 0 ${i2.x} ${i2.y}`,
      "Z",
    ].join(" ");
  }

  const largeArc = sweep > 180 ? 1 : 0;
  const oStart = polar(cx, cy, outerR, startAngle);
  const oEnd = polar(cx, cy, outerR, endAngle);
  const iEnd = polar(cx, cy, innerR, endAngle);
  const iStart = polar(cx, cy, innerR, startAngle);
  return [
    `M ${oStart.x} ${oStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${oEnd.x} ${oEnd.y}`,
    `L ${iEnd.x} ${iEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${iStart.x} ${iStart.y}`,
    "Z",
  ].join(" ");
}

function buildSlices(items: CustomerRevenuePieItem[], totalRevenue: number): Slice[] {
  const positive = items
    .map((item) => ({ ...item, value: Math.max(0, item.value) }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  if (positive.length === 0 || totalRevenue <= 0) return [];

  const top = positive.slice(0, TOP_SLICE_COUNT);
  const topSum = top.reduce((sum, item) => sum + item.value, 0);
  const rest = Math.max(0, totalRevenue - topSum);

  const raw: CustomerRevenuePieItem[] = [...top];
  if (rest > 0.005) {
    raw.push({ key: "__other__", label: "Sonstige", value: rest });
  }

  // Prozentanteile am Jahresumsatz; Rundungsreste am letzten Slice ausgleichen
  const percents = raw.map((item) => (item.value / totalRevenue) * 100);
  const rounded = percents.map((p) => Math.round(p * 10) / 10);
  const drift = Math.round((100 - rounded.reduce((s, p) => s + p, 0)) * 10) / 10;
  if (rounded.length > 0 && Math.abs(drift) >= 0.05) {
    rounded[rounded.length - 1] = Math.round((rounded[rounded.length - 1]! + drift) * 10) / 10;
  }

  const cx = 100;
  const cy = 100;
  const outerR = 90;
  const innerR = 52;
  let angle = 0;

  return raw.map((item, index) => {
    const percent = rounded[index] ?? 0;
    const sweep = (item.value / totalRevenue) * 360;
    const startAngle = angle;
    const endAngle = angle + sweep;
    angle = endAngle;
    return {
      key: item.key,
      label: item.label,
      value: item.value,
      percent,
      color: SLICE_COLORS[index % SLICE_COLORS.length]!,
      path: donutSegmentPath(cx, cy, outerR, innerR, startAngle, endAngle),
    };
  });
}

export function CustomerRevenuePie({
  items,
  totalRevenue,
  emptyText = "Keine Kundendaten vorhanden.",
}: {
  items: CustomerRevenuePieItem[];
  /** Bezugsgröße für Prozentanteile (Jahresumsatz). */
  totalRevenue: number;
  emptyText?: string;
}) {
  const slices = useMemo(() => buildSlices(items, totalRevenue), [items, totalRevenue]);

  if (slices.length === 0) {
    return <p className="text-sm text-label-secondary">{emptyText}</p>;
  }

  return (
    <div className="eh-docreport-pie">
      <div className="eh-docreport-pie__chart" role="img" aria-label="Top-Kunden nach Umsatz">
        <svg viewBox="0 0 200 200" className="eh-docreport-pie__svg" aria-hidden>
          {slices.map((slice) => (
            <path
              key={slice.key}
              d={slice.path}
              fill={slice.color}
              className="eh-docreport-pie__slice"
            >
              <title>
                {`${slice.label}: ${formatEuroCompact(slice.value)} (${formatPercentDe(slice.percent)})`}
              </title>
            </path>
          ))}
        </svg>
        <div className="eh-docreport-pie__center">
          <span className="eh-docreport-pie__center-label">Gesamt</span>
          <span className="eh-docreport-pie__center-value tabular-nums">
            {formatEuroCompact(totalRevenue)}
          </span>
        </div>
      </div>

      <ul className="eh-docreport-pie__legend">
        {slices.map((slice) => (
          <li key={slice.key} className="eh-docreport-pie__legend-item">
            <span
              className="eh-docreport-pie__swatch"
              style={{ background: slice.color }}
              aria-hidden
            />
            <span className="eh-docreport-pie__legend-label" title={slice.label}>
              {slice.label}
            </span>
            <span className="eh-docreport-pie__legend-percent tabular-nums">
              {formatPercentDe(slice.percent)}
            </span>
            <span className="eh-docreport-pie__legend-value tabular-nums">
              {formatEuroCompact(slice.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
